// Состояние приложения и базовые операции над ним, общие для всех модулей UI: партия (G), настройки экрана (ui),
// сетевой клиент, корневые контейнеры, окна. Правило: всё видимое — функция (G, ui); DOM руками не правится.
import { render as mount } from 'preact';
import type { JSX } from 'preact';
import type { BossStat, CardRef, CorrList, DailyInfo, Game as GameState, Iid, Leaderboards, PlayerId, PresenceSnapshot, PushDevice, RatingChange } from '../types.ts';
import { player } from '../engine/state.ts';
import { vpBonusOf, undoSnapshot } from '../engine/game.ts';
import type { NetClient } from './net.ts';
import type { PresenceClient } from './presence.ts';
import { esc, setVpContext, setRebalanceContext } from './cards.ts';
import { findCard as findCardIn, myPrompt as myPromptOf } from './board.ts';
import { notify } from './notify.ts';
import { initialUi } from './state.ts';
import type { GameUi, UiState } from './state.ts';
import { saveGame } from './storage.ts';
import { mustId, q } from './dom.ts';

/** Рейтинги с сервера: последний ответ, идёт ли загрузка, не удалась ли последняя */
export interface LeadersState {
  data: Leaderboards | null;
  loading: boolean;
  error: boolean;
}

/** Сдвиг в рейтинге за только что законченную партию (экран итога): для какого сида, ответ, идёт ли загрузка */
export interface RatingState {
  seed: number | null;
  data: RatingChange | null;
  loading: boolean;
}

/** Сид дня с сервера: стол дня, моя попытка, таблицы; startErr — почему не удалось начать попытку */
export interface DailyState {
  data: DailyInfo | null;
  loading: boolean;
  error: boolean;
  startErr: string;
  /** идёт регистрация попытки на сервере */
  starting: boolean;
}

/** Партии по переписке с сервера: мои и открытые наборы; busy — идёт создание или вход */
export interface CorrState {
  data: CorrList | null;
  loading: boolean;
  error: boolean;
  busy: boolean;
}

/**
 * Уведомления этого устройства: unsupported — браузер не умеет (iosBrowser — iPhone не из приложения на экране «Домой»),
 * denied — запрещены в настройках браузера, off — не включены, on — подписка на сервере; devices — устройства
 * аккаунта с уведомлениями (профиль)
 */
export type PushStatus = 'unknown' | 'unsupported' | 'iosBrowser' | 'denied' | 'off' | 'on';
export interface PushState {
  status: PushStatus;
  busy: boolean;
  error: string;
  devices: PushDevice[] | null;
}

/** Мои встречи с боссами (экран дуэли, плитка на хабе): из своего профиля на сервере */
export interface MyBossesState {
  data: BossStat[] | null;
  loading: boolean;
  error: boolean;
}

/** Изменяемое состояние приложения: полное состояние партии (хотсит) или представление игрока (онлайн), клиент комнаты,
 *  клиент присутствия и его последний снимок (кто онлайн, открытые лобби; null — сервера нет или связь оборвана) */
export const st: { G: GameState | null; net: NetClient | null; pendingRender: boolean; pres: PresenceClient | null; presence: PresenceSnapshot | null; leaders: LeadersState; rating: RatingState; daily: DailyState; bosses: MyBossesState; corr: CorrState; push: PushState } =
  { G: null, net: null, pendingRender: false, pres: null, presence: null, leaders: { data: null, loading: false, error: false }, rating: { seed: null, data: null, loading: false }, daily: { data: null, loading: false, error: false, startErr: '', starting: false }, bosses: { data: null, loading: false, error: false },
    corr: { data: null, loading: false, error: false, busy: false }, push: { status: 'unknown', busy: false, error: '', devices: null } };
export const ui: UiState = initialUi();

export const $app = mustId('app');
export const $modal = mustId('modal');
export const $ = <T extends HTMLElement = HTMLElement>(s: string): T | null => q<T>($app, s);

// ---------- инварианты ----------
export function requireG(): GameState {
  if (!st.G) throw new Error('Нет партии');
  return st.G;
}
export function hasViewer(u: UiState): u is GameUi {
  return u.viewer != null;
}
export function gameUi(): GameUi {
  if (!hasViewer(ui)) throw new Error('Не определено, кто смотрит на экран');
  return ui;
}
export function viewerId(): PlayerId {
  return gameUi().viewer;
}
// бонус ПО на картах — глазами смотрящего: «что эта карта принесёт мне» (жетон свойства, Гусыня, Гикпиг, палочки)
setVpContext((c) => {
  if (!st.G || st.G.phase !== 'play' || !hasViewer(ui)) return null;
  return vpBonusOf(st.G, player(st.G, ui.viewer), c);
});
// в партии режима ребаланса правленые карты помечаются значком ⚖️ и сноской
setRebalanceContext(() => !!st.G?.rebalance);

// ---------- утилиты ----------
/** Сообщение пользователю — в общую ленту уведомлений (очередь, ничего не накладывается) */
export function toast(msg: string, ms = 2500): void {
  notify({ html: esc(msg), cls: 'n-toast', hold: ms, priority: 'high' });
}
export function save(): void {
  // плеер показывает чужую запись: сохранённую хотсит-партию затирать нельзя
  if (st.net || !st.G || ui.replay) return;
  // вместе с партией кладём снимок для «шага назад»: иначе после закрытия приложения отменить нечем
  saveGame({ G: st.G, undoBase: undoSnapshot(st.G), viewer: ui.viewer, screen: ui.screen, setup: ui.setup, seenSeq: ui.seenSeq });
}
export const findCard = (iid: Iid): CardRef | null => (st.G ? findCardIn(st.G, iid) : null);
export const isMyTurn = (): boolean => !ui.replay && !!st.G && st.G.phase === 'play' && !!st.G.turn && ui.viewer === st.G.turn.player;
export const canAct = (): boolean => isMyTurn() && !!st.G && !st.G.prompt && st.G.promptFor == null;
export const myPrompt = (): ReturnType<typeof myPromptOf> => (st.G && ui.viewer != null && !ui.replay ? myPromptOf(st.G, ui.viewer) : null);

// ---------- контейнеры ----------
/** Экран — diff'ом в #app; на старте контейнер с «Загрузка…» вычищается первым же render */
export const paint = (v: JSX.Element | null): void => { mount(v, $app); };
/** Окно строится из состояния заново при каждой перерисовке (степпер чипсин, «Оригинал») — хранится строитель */
export type ModalBuild = () => JSX.Element;
let modalBuild: ModalBuild | null = null;
export const openModal = (build: ModalBuild): void => { modalBuild = build; mount(build(), $modal); };
export const repaintModal = (): void => { if (modalBuild) mount(modalBuild(), $modal); };
export const closeModal = (): void => { modalBuild = null; mount(null, $modal); };
export const modalOpen = (): boolean => !!modalBuild;
/** Текущее окно — чтобы вернуться к нему после карточки, открытой поверх */
export const currentModal = (): ModalBuild | null => modalBuild;
